import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatProgressSpinner } from '@angular/material/progress-spinner'

import { ServiceOrderService } from '../../../core/services/service/service-order/service-order.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import {
  descricaoAndamento,
  HistoricoAndamentoVm,
  STATUS_ANDAMENTO_NAO_INICIADA,
  STATUS_ANDAMENTO_PARADA,
  TRANSICOES_ANDAMENTO,
} from '../../../core/models/monitoring-os.model'

@Component({
  selector: 'modal-progresso-os',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinner,
  ],
  templateUrl: './modal-progresso-os.component.html',
  styleUrl: './modal-progresso-os.component.scss',
})
export class ModalProgressoOsComponent implements OnInit {
  @Output() response = new EventEmitter<boolean>()

  constructor(
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { item: any },
    private dialogRef: MatDialogRef<ModalProgressoOsComponent>,
    private serviceOrderService: ServiceOrderService,
    private snackbar: SnackbarCustomService,
  ) { }

  form!: FormGroup
  historico: HistoricoAndamentoVm[] = []
  paradas: any[] = []

  loadingHistorico = false
  loadingParadas = false
  advancing = false
  saving = false
  changed = false

  // Etapa em vigor — atualizada localmente a cada avanço/gravação bem-sucedidos,
  // sem precisar fechar e reabrir o modal.
  etapaAtual!: number

  descricaoAndamento = descricaoAndamento
  STATUS_ANDAMENTO_PARADA = STATUS_ANDAMENTO_PARADA

  get item() {
    return this.data.item
  }

  get proximasEtapas(): number[] {
    return TRANSICOES_ANDAMENTO[this.etapaAtual] ?? []
  }

  get faltaRecursoParaIniciar(): boolean {
    return this.etapaAtual === STATUS_ANDAMENTO_NAO_INICIADA && (!this.item?.nome || !this.item?.placa)
  }

  ngOnInit(): void {
    this.etapaAtual = this.item?.statusOsAndamentoId ?? STATUS_ANDAMENTO_NAO_INICIADA
    this.createForm()
    this.loadHistorico()
    this.loadParadas()
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      statusId: [null, [Validators.required]],
      itinerarioId: [null],
      observacao: [''],
      dataOcorrido: [null],
      horaOcorrido: [''],
    })
    this.form.get('statusId')!.valueChanges.subscribe((statusId: number) => {
      const itinerarioControl = this.form.get('itinerarioId')!
      if (statusId === STATUS_ANDAMENTO_PARADA) {
        itinerarioControl.setValidators([Validators.required])
      } else {
        itinerarioControl.clearValidators()
        itinerarioControl.setValue(null)
      }
      itinerarioControl.updateValueAndValidity()
    })
  }

  loadHistorico(): void {
    this.loadingHistorico = true
    this.serviceOrderService.getHistoricoAndamento(this.item.codigoOs).subscribe({
      next: (res: any) => { this.historico = res.data ?? [] },
      error: (err: any) => { this.snackbar.errorRequest(err) },
      complete: () => { this.loadingHistorico = false },
    })
  }

  // Só "trecho P" são paradas de fato — "O"/"D" são origem e destino.
  loadParadas(): void {
    this.loadingParadas = true
    this.serviceOrderService.getServiceOrderItineraryStopbyId(this.item.codigoOs, 50, 1).subscribe({
      next: (res: any) => {
        this.paradas = (res.data ?? []).filter((p: any) => (p.trecho ?? 'P') === 'P')
      },
      error: (err: any) => { this.snackbar.errorRequest(err) },
      complete: () => { this.loadingParadas = false },
    })
  }

  // A API decide sozinha a próxima etapa (inclusive Parada/Destino) e aceita a OS
  // quando ela está Agendada. Não manda status nenhum.
  avancarEtapa(): void {
    this.advancing = true
    this.serviceOrderService.checkAndamento(this.item.codigoOs).subscribe({
      next: (res: any) => { this.afterSuccess(res) },
      error: (err: any) => { this.advancing = false; this.snackbar.errorRequest(err) },
    })
  }

  salvarManual(): void {
    if (this.form.invalid) return
    this.saving = true
    const v = this.form.value
    const model: any = {
      id: this.item.codigoOs,
      statusId: v.statusId,
    }
    if (v.statusId === STATUS_ANDAMENTO_PARADA) model.itinerarioId = v.itinerarioId
    if (v.observacao) model.observacao = v.observacao
    if (v.dataOcorrido) model.dataOcorrido = this.formatDate(v.dataOcorrido)
    if (v.horaOcorrido) model.horaOcorrido = v.horaOcorrido

    this.serviceOrderService.editStatusAndamento(model).subscribe({
      next: (res: any) => { this.afterSuccess(res) },
      error: (err: any) => { this.saving = false; this.snackbar.errorRequest(err) },
    })
  }

  private afterSuccess(res: any): void {
    this.advancing = false
    this.saving = false
    this.changed = true
    this.snackbar.success(res.message ?? 'Progresso atualizado com sucesso.')
    this.etapaAtual = res.data?.statusOsAndamentoId ?? this.etapaAtual
    this.form.reset({ statusId: null, itinerarioId: null, observacao: '', dataOcorrido: null, horaOcorrido: '' })
    this.loadHistorico()
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  // Comparação previsto x realizado numa linha de Parada — a medida de atraso por
  // ponto do trajeto.
  atrasoParada(h: HistoricoAndamentoVm): string | null {
    if (!h.horaParadaPrevista || !h.horaOcorrido) return null
    return h.horaParadaPrevista === h.horaOcorrido ? null : `previsto ${h.horaParadaPrevista} · realizado ${h.horaOcorrido}`
  }

  // Diferença entre dataCriacao/horaCriacao (quando entrou no sistema) e
  // dataOcorrido/horaOcorrido (quando aconteceu de fato) indica registro offline
  // sincronizado depois pelo app.
  registradoOffline(h: HistoricoAndamentoVm): boolean {
    return h.dataCriacao !== h.dataOcorrido || h.horaCriacao !== h.horaOcorrido
  }

  close(): void {
    this.response.emit(this.changed)
    this.dialogRef.close()
  }
}
